import React, { useMemo, useRef, useState } from "react";
import { usePanZoom } from "./usePanZoom";

function edgePath(from, to) {
  const x1 = from.x + from.width;
  const y1 = from.y + from.height / 2;
  const x2 = to.x;
  const y2 = to.y + to.height / 2;
  const dx = Math.max(40, (x2 - x1) * 0.5);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
}

function nodeFill(type, selected) {
  if (selected) return "#FFF3EA";
  if (type === "root") return "#FF781F";
  return "#FFFFFF";
}

function nodeStroke(type, selected) {
  if (selected) return "#FF781F";
  if (type === "root") return "#FF781F";
  return "#E5E7EB";
}

function textColor(type) {
  if (type === "root") return "#FFFFFF";
  return "#111827";
}

export default function TreeHierarchyChart({
  layout,
  onNodeClick,
  selectedNodeId,
  onNodeSelect,
}) {
  const containerRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const activeSelectedId = selectedNodeId ?? selectedId;

  const nodeById = useMemo(() => {
    const m = new Map();
    (layout?.nodes || []).forEach((n) => m.set(n.id, n));
    return m;
  }, [layout]);

  const {
    svgTransform,
    handlers,
    onWheel,
    zoomIn,
    zoomOut,
    reset,
  } = usePanZoom({ initialX: 24, initialY: 24, initialScale: 1, minScale: 0.35, maxScale: 2.8 });

  const getRect = () => containerRef.current?.getBoundingClientRect?.() || null;

  const handleWheel = (e) => {
    const rect = getRect();
    if (!rect) return;
    onWheel(e, rect);
  };

  const handleZoomIn = () => {
    const rect = getRect();
    if (!rect) return;
    zoomIn(rect);
  };

  const handleZoomOut = () => {
    const rect = getRect();
    if (!rect) return;
    zoomOut(rect);
  };

  const handleNodeClick = (node) => {
    if (typeof onNodeSelect === "function") onNodeSelect(node.id);
    else setSelectedId(node.id);
    if (typeof onNodeClick === "function") onNodeClick(node.payload);
  };

  const dragHandlers = {
    onPointerDown: (e) => {
      setIsPanning(true);
      handlers.onPointerDown(e);
    },
    onPointerMove: handlers.onPointerMove,
    onPointerUp: (e) => {
      setIsPanning(false);
      handlers.onPointerUp(e);
    },
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-[70vh] min-h-[520px] overflow-hidden bg-white rounded-md border border-gray-200"
      style={{ userSelect: "none", WebkitUserSelect: "none" }}
    >
      <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
        <button className="px-3 py-1 rounded border border-gray-200 bg-white text-sm" onClick={handleZoomIn} type="button">
          +
        </button>
        <button className="px-3 py-1 rounded border border-gray-200 bg-white text-sm" onClick={handleZoomOut} type="button">
          −
        </button>
        <button className="px-3 py-1 rounded border border-gray-200 bg-white text-sm" onClick={reset} type="button">
          Reset
        </button>
      </div>

      <svg
        className="w-full h-full"
        onWheel={handleWheel}
        style={{ cursor: isPanning ? "grabbing" : "grab" }}
      >
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="transparent"
          pointerEvents="all"
          {...dragHandlers}
        />
        <g transform={svgTransform}>
          {(layout?.edges || []).map((e) => {
            const from = nodeById.get(e.from);
            const to = nodeById.get(e.to);
            if (!from || !to) return null;
            return <path key={e.id} d={edgePath(from, to)} fill="none" stroke="#CBD5E1" strokeWidth="2" />;
          })}

          {(layout?.nodes || []).map((n) => {
            const selected = activeSelectedId === n.id;
            return (
              <g
                key={n.id}
                onClick={() => handleNodeClick(n)}
                onPointerDown={(e) => e.stopPropagation()}
                style={{ cursor: "pointer" }}
              >
                <rect
                  x={n.x}
                  y={n.y}
                  width={n.width}
                  height={n.height}
                  rx="12"
                  fill={nodeFill(n.type, selected)}
                  stroke={nodeStroke(n.type, selected)}
                  strokeWidth={n.type === "root" ? 2 : 1}
                />
                <text
                  x={n.x + 12}
                  y={n.y + 28}
                  fill={textColor(n.type)}
                  fontSize="13"
                  fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto"
                >
                  {String(n.label || "")}
                </text>
                <text
                  x={n.x + 12}
                  y={n.y + 52}
                  fill={textColor(n.type)}
                  fontSize="18"
                  fontWeight="700"
                  fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto"
                >
                  {String(n.value ?? "")}
                </text>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}
